import { Link, createFileRoute } from '@tanstack/react-router';
import { ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { AuroraBackground } from '@/components/shared/AuroraBackground';
import { api, type MeResponse } from '@/api/api';

export const Route = createFileRoute('/')({
  loader: (): Promise<MeResponse | null> => api.getMeSilently(),
  component: HomePage,
});

const BEHAVIORS = [
  'See per-member utilization before you commit to a date.',
  'Update task status inline. No dialog, no full-page reload.',
  'Screenshot the live Gantt instead of maintaining a slide.',
];

function HomePage() {
  const me = Route.useLoaderData();

  return (
    <>
      <AuroraBackground />
      <main className="relative z-10 flex min-h-screen flex-col text-aurora-text">
        <header className="px-8 pt-8 sm:px-12 sm:pt-10">
          <span className="text-sm font-medium tracking-tight text-aurora-text">
            PM Dashboard
          </span>
        </header>

        <section className="flex flex-1 items-center px-8 py-16 sm:px-12 sm:py-20">
          <div className="w-full max-w-2xl">
            <div className="mb-10 space-y-5">
              <h1 className="text-4xl font-medium leading-[1.05] tracking-tight text-aurora-text sm:text-5xl">
                One place for project delivery.
              </h1>
              <p className="max-w-prose text-base leading-relaxed text-aurora-text-muted">
                Timeline, tasks, milestones, and per-member workload, kept in
                sync the moment anything changes. Replaces the Excel, Jira, and
                PowerPoint files that drift apart within hours of being saved.
              </p>
            </div>

            <ol className="mb-12 space-y-3 text-sm">
              {BEHAVIORS.map((line, i) => (
                <li key={line} className="flex gap-4">
                  <span
                    aria-hidden="true"
                    className="w-5 shrink-0 pt-0.5 text-xs tabular-nums text-aurora-text-faint"
                  >
                    {String(i + 1).padStart(2, '0')}
                  </span>
                  <span className="text-aurora-text-muted">{line}</span>
                </li>
              ))}
            </ol>

            <div className="flex flex-wrap items-center gap-4">
              {me ? (
                <>
                  <Button asChild variant="primary">
                    <Link to="/projects">
                      Open dashboard
                      <ArrowRight className="h-3.5 w-3.5" aria-hidden="true" />
                    </Link>
                  </Button>
                  <p className="text-xs text-aurora-text-faint">
                    Signed in as {me.display_name}.
                  </p>
                </>
              ) : (
                <>
                  <Button asChild variant="primary">
                    <Link to="/sign-in">
                      Sign in
                      <ArrowRight className="h-3.5 w-3.5" aria-hidden="true" />
                    </Link>
                  </Button>
                  <p className="text-xs text-aurora-text-faint">
                    Internal access only. Ask your project&apos;s PM to add you.
                  </p>
                </>
              )}
            </div>
          </div>
        </section>

        <footer className="px-8 pb-8 sm:px-12 sm:pb-10">
          <p className="text-xs tabular-nums text-aurora-text-faint">
            Accenture internal preview
          </p>
        </footer>
      </main>
    </>
  );
}
